<template>
  <el-form
    ref="formRef"
    require-asterisk-position="right"
    label-position="top"
    :model="formValues"
    :rules="rules"
  >
    <div style="display: flex">
      <div class="oa-form-left">
        <el-form-item prop="bankAccountOpenLicenseUrl" label="法定代表人银行卡">
          <card-image-upload
            :action="uploadUrl"
            :image="formValues.bankAccountOpenLicenseUrl"
            :readonly="disabledAll"
            @on-success="(img: string) => {
              formValues.bankAccountOpenLicenseUrl = img;
              formRef?.validateField?.('bankAccountOpenLicenseUrl');
            }"
          />
        </el-form-item>
        <el-form-item prop="bankCode" label="开户银行">
          <el-select
            v-model="formValues.bankCode"
            clearable
            filterable
            placeholder="请选择开户银行"
            autocomplete="noy"
            :filter-method="filterBankCode"
            @change="selectBasicBank"
            @keyup.enter="onKeyupEnter"
          >
            <el-option
              v-for="item in basicBankList"
              :key="item.node.id"
              :label="item.node.bank_name"
              :value="item.node.bank_code"
            />
          </el-select>
        </el-form-item>
        <el-form-item prop="phoneNo" label="银行预留手机号">
          <el-input
            v-model="formValues.phoneNo"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabledAll"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
      </div>

      <div class="oa-form-right">
        <el-form-item label="xx" style="opacity: 0;margin-bottom: 189px;">
          &nbsp;
        </el-form-item>
        <el-form-item prop="bankNo" label="银行账号">
          <el-input
            v-model="formValues.bankNo"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabledAll"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
      </div>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { CardImageUpload } from '@vue3/components';
import { FormInstance, FormRules } from 'element-plus';
import { computed, PropType, ref, watch } from 'vue';
import { bankCode as banks, checkPhone, scrollToErrorField } from '@core/tools';
import { ICnapsCode } from '../../../../types/lianlian';
import { IAccOpening } from '../account-opening/type';

const uploadUrl = '/iam/payment-account/upload';

const props = defineProps({
  data: {
    type: Object as PropType<IAccOpening>,
    default: () => {
      return {} as IAccOpening;
    },
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  disabledAll: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{'key-enter': []}>();

const formRef = ref<FormInstance>();

const formValues = ref({} as IAccOpening);
const basicBankList = ref(banks);
const bankBranchList = ref<ICnapsCode[]>([]);

const rules = computed<FormRules<IAccOpening>>(() => {
  return {
    bankAccountOpenLicenseUrl: !props.disabledAll
      ? [
        {
          required: true,
          message: '请上传法定代表人银行卡',
        },
      ]
      : undefined,
    bankCode: [
      {
        required: true,
        message: '请选择开户银行',
      },
    ],
    bankNo: !props.disabledAll
      ? [
        {
          required: true,
          message: '请输入银行账号',
          trigger: 'blur',
        },
      ]
      : undefined,
    phoneNo: !props.disabledAll
      ? [
        {
          required: true,
          validator: (rule: any, value: string, callback: any) => {
            if (checkPhone(formValues.value.phoneNo, '银行预留')) {
              callback(checkPhone(formValues.value.phoneNo, '银行预留'));
            }
            callback();
          },
        },
      ]
      : undefined,
  };
});

function filterBankCode(name: string) {
  basicBankList.value = banks.filter((item) => item.node.bank_name.includes(name));
}

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    } else {
      if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    }
  });

  return valid;
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const valid = await validate();

  if (!valid) return;

  if (!valid) {
    if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    return;
  }

  emit('key-enter');
}

function selectBasicBank(bankcode: string) {
  const list = banks || [];
  const result = list ? list.filter((v) => v.node.bank_code === bankcode) : [];

  if (result.length > 0) {
    const { node } = result[0];

    // 开户银行赋值（名称和code）
    bankBranchList.value = [];
    formValues.value.bankCode = node.bank_code;
  }
}

function resetFormValues() {
  const d = JSON.parse(JSON.stringify(props.data)) as IAccOpening;

  formRef.value?.resetFields?.();
  formValues.value = {
    bankAccountOpenLicenseUrl: d.bankAccountOpenLicenseUrl,
    bankCode: d.bankCode,
    bankNo: d.bankNo,
    phoneNo: d.phoneNo,
  };
}

watch(
  () => props.data,
  () => {
    resetFormValues();
  },
  { immediate: true },
);

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => formValues.value,
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});
</script>

<style scoped lang="scss" src="../index.scss" />

